import { Alert, Badge, Heading, IconButton, Link, Pagination, Pane, RefreshIcon, Spinner, Table, Text } from 'evergreen-ui';
import moment from 'moment';
import { Component } from 'react';
import Api from '../../../util/Api';
import { AlertBlock, BackorderBlock, CompanyBlock, PreorderBlock, ReviewBlock, SinglesBlock, StorePickupBlock } from '../../orders/components/ComponentBlocks';

class OrdersToProcess extends Component {
  constructor(props) {
    super(props);
    this.state = {
      orders: [],
      loading: true,
      error: null,
      pagination: {
        page: 1,
        limit: 10,
        totalCount: 0,
        totalPages: 0
      }
    };
  }

  componentDidMount() {
    this.fetchOrdersToProcess();
    // Refresh every minute
    this.refreshInterval = setInterval(() => {
      this.fetchOrdersToProcess(this.state.pagination.page);
    }, 60000);
  }

  componentWillUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  fetchOrdersToProcess = async (page = 1) => {
    try {
      this.setState({ loading: true, error: null });
      const { limit } = this.state.pagination;
      const response = await Api.get(`/dashboard/orders-to-process?page=${page}&limit=${limit}`);

      if (response.success) {
        this.setState({
          orders: response.orders,
          pagination: response.pagination,
          loading: false
        });
      } else {
        this.setState({ error: 'Failed to fetch orders', loading: false });
      }
    } catch (error) {
      console.error('Error fetching orders to process:', error);
      this.setState({
        error: 'Failed to load orders',
        loading: false
      });
    }
  };

  handlePageChange = (page) => {
    this.fetchOrdersToProcess(page);
  };

  getStatusBadgeColor(status) {
    switch (status) {
      case 'placed':
        return 'red';
      case 'confirmed':
        return 'orange';
      case 'processed':
        return 'yellow';
      case 'delivered':
        return 'teal';
      case 'completed':
        return 'green';
      case 'cancelled':
        return 'neutral';
      case 'returning':
        return 'teal';
      case 'returned':
        return 'purple';
      default:
        return 'neutral';
    }
  }

  getPaymentStatusBadgeColor(paymentStatus) {
    switch (paymentStatus) {
      case 'paid':
        return 'green';
      case 'unpaid':
        return 'red';
      case 'payment-failed':
        return 'red';
      default:
        return 'neutral';
    }
  }

  getDeliveryLabel(delivery) {
    switch (delivery) {
      case 'delivery':
        return 'Courier';
      case 'personal':
        return 'Pickup';
      case 'personal-brasov':
        return 'Pickup Brasov';
      case 'delivery-locker':
        return 'Locker';
      default:
        return delivery;
    }
  }

  render() {
    const { orders, loading, error, pagination } = this.state;

    return (
      <Pane>
        <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={16}>
          <Heading size={600}>
            Orders to Process ({pagination.totalCount})
          </Heading>
          <IconButton
            icon={RefreshIcon}
            onClick={() => this.fetchOrdersToProcess(pagination.page)}
            disabled={loading}
          />
        </Pane>

        {loading && (
          <Pane display="flex" alignItems="center" justifyContent="center" padding={40}>
            <Spinner />
          </Pane>
        )}

        {error && !loading && (
          <Pane padding={16}>
            <Alert intent="danger" title="Error loading orders">
              {error}
            </Alert>
          </Pane>
        )}

        {!loading && !error && (!orders || orders.length === 0) && (
          <Pane padding={16}>
            <Alert intent="success" title="All caught up!">
              There are no orders waiting to be processed.
            </Alert>
          </Pane>
        )}

        {!loading && !error && orders && orders.length > 0 && (
          <>
            <Table>
              <Table.Head>
                <Table.TextHeaderCell>Order #</Table.TextHeaderCell>
                <Table.TextHeaderCell>Tags</Table.TextHeaderCell>
                <Table.TextHeaderCell>Customer</Table.TextHeaderCell>
                <Table.TextHeaderCell>Status</Table.TextHeaderCell>
                <Table.TextHeaderCell>Payment</Table.TextHeaderCell>
                <Table.TextHeaderCell>Delivery</Table.TextHeaderCell>
                <Table.TextHeaderCell>Total</Table.TextHeaderCell>
                <Table.TextHeaderCell>Date</Table.TextHeaderCell>
                <Table.TextHeaderCell>Actions</Table.TextHeaderCell>
              </Table.Head>
              <Table.Body>
                {orders.map((order) => (
                  <Table.Row key={order.id}>
                    <Table.TextCell>
                      <Link href={`/orders/${order.id}`} target="_blank" rel="noopener noreferrer">
                        #{order.orderNumber}
                      </Link>
                      <Pane marginLeft={8} display="inline"><ReviewBlock {...order} /></Pane>
                    </Table.TextCell>
                    <Table.TextCell>
                      <Pane display="flex" alignItems="center" gap={4}>
                        <PreorderBlock {...order} />
                        <BackorderBlock {...order} />
                        <SinglesBlock {...order} />
                        <AlertBlock {...order} />
                        <StorePickupBlock {...order} />
                        <CompanyBlock {...order} />
                      </Pane>
                    </Table.TextCell>
                    <Table.TextCell>
                      {order.user ? (
                        <Text size={400}>
                          {order.user.name}
                        </Text>
                      ) : (
                        <Text size={400}>{order.deliveryName || 'Guest'}</Text>
                      )}
                    </Table.TextCell>
                    <Table.TextCell>
                      <Badge color={this.getStatusBadgeColor(order.status)}>
                        {order.status}
                      </Badge>
                    </Table.TextCell>
                    <Table.TextCell>
                      <Badge color={this.getPaymentStatusBadgeColor(order.paymentStatus)}>
                        {order.paymentStatus}
                      </Badge>
                    </Table.TextCell>
                    <Table.TextCell>
                      <Text size={400}>{this.getDeliveryLabel(order.delivery)}</Text>
                    </Table.TextCell>
                    <Table.TextCell>
                      <Text size={400}>{order.grandTotal?.toFixed(2)} RON</Text>
                    </Table.TextCell>
                    <Table.TextCell>
                      <Text size={400}>
                        {moment(order.createdAt).format('DD/MM/YYYY HH:mm')}
                      </Text>
                    </Table.TextCell>
                    <Table.TextCell>
                      <Link href={`/orders/${order.id}`} target="_blank" rel="noopener noreferrer">
                        View
                      </Link>
                    </Table.TextCell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>

            {pagination.totalPages > 1 && (
              <Pane display="flex" justifyContent="center" marginTop={16}>
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={this.handlePageChange}
                />
              </Pane>
            )}
          </>
        )}
      </Pane>
    );
  }
}

export default OrdersToProcess;
